<template>
  <div>
    <Banner />
    <div class="container-fluid">
      <div class="container">

        <ReviewModeInfo class="mb-4" />

        <div class="row" v-if="error">
          <div class="page-content-state col-12">
            <span class="page-content-state__icon fal fa-sad-cry"></span>
            <div class="page-content-state__header">Sorry, there was a problem loading your Saved Course Plan.</div>
            <div class="page-content-state__content">Please check that you have correctly copied your link and try again.</div>
            <button class="btn btn-primary mt-3" id="tooltip-regenerate-btn" v-on:click="loadPlan">Try again</button>
          </div>
        </div>
        <div class="row" v-else-if="loading">
          <div class="page-content-state page-content-state-loading col-12">
            <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
            <div class="page-content-state__content">Loading Saved Course Plan</div>
          </div>
        </div>
        <div class="row" v-else-if="noPlan">
          <div class="page-content-state page-content-state-loading col-12">
            <div class="page-content-state__content">Saved Course not found.</div>
          </div>
        </div>
        <div class="row" v-else-if="requiresLogin">
          <div class="col-12">
            <div class="page-alert warning mb-4">
              <i class="page-alert__icon far fa-exclamation-circle"></i>
              <div class="page-alert__body" style="width: 100%">
                <div class="float-right">
                  <div v-if="savedPlan.status && savedPlan.status != 'Active'" class="badge badge-primary">{{statusLabel(savedPlan.status)}}</div>
                  <div class="badge badge-secondary">Version {{savedPlan.courseLoopVersion}}</div>
                </div>

                <p>This saved plan includes draft content for course {{ savedPlan.courseCode }}.</p>
                <p>Please log in to review it.</p>
                <button class="btn btn-primary mt-3" v-on:click="doLogin">Staff Login</button>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-else>
          <div class="col-12">
            <SavedPlanSummary v-if="savedPlan" :savedPlan="savedPlan" />
            <div class="row">
              <div class="col-lg-7 col-6">
                <h2 class="h3 mb-2">{{ __('label:create-plan title') }}</h2>
                <h5 v-if="savedPlan && savedPlan.coursePlan.years && savedPlan.coursePlan.years.length"><i class="far fa-calendar-alt"></i> Duration:
                  {{ savedPlan.coursePlan.years.length }}
                  {{ savedPlan.coursePlan.years.length > 1 ? 'years' : 'year' }}</h5>
                <p class="mb-0">This course plan was generated <strong>{{formatDateTime}} AWST</strong></p>
                <p>Below is a read only version of your saved course plan.</p>
              </div>

              <div class="col-lg-5 col-6" v-if="savedPlan && validating">
                <div class="page-content-state page-content-state-loading col-12 pt-1">
                  <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
                  <div class="page-content-state__content">Please wait while we attempt to validate your {{currentValidationTask}}...</div>
                </div>
              </div>
              <div class="col-lg-5 col-6 d-flex flex-column align-items-end" v-if="savedPlan && !validating">

                <div v-if="this.validationStates.draftNoLonger" class="page-alert mb-2 info w-100">
                  <i class="page-alert__icon far fa-info-circle"></i>
                  <div class="page-alert__body">
                    The saved plan <span class="badge badge-primary">{{__('label:status draft')}}</span> <span class="badge badge-secondary">Version {{ savedPlan.courseLoopVersion }}</span> no longer exists.<br/>
                    Defaulting to <span class="badge" :class="getSelectedCourse.status != 'Active' ? 'badge-primary' : 'badge-success'">{{ statusLabel(getSelectedCourse.status) }}</span> <span class="badge badge-secondary">Version {{ getSelectedCourse.courseLoopVersionNumber }}</span>.
                  </div>
                </div>

                <!-- danger state -->
                <div v-if="this.validationStates.networkError" class="page-alert mb-0 danger w-100">
                  <i class="page-alert__icon far fa-times-circle"></i>
                  <div class="page-alert__body">
                    <p>
                      There was an error checking your saved course plan. Please reload to try again.
                    </p>
                  </div>
                </div>
                <div v-else-if="!this.validationStates.courseValid" class="page-alert mb-0 danger w-100">
                  <i class="page-alert__icon far fa-times-circle"></i>
                  <div class="page-alert__body">
                    <p>
                      Your saved course is no longer offered, and this plan is now invalid. <br />
                      Please choose another course.
                    </p>
                  </div>
                </div>

                <!-- warning state -->
                <!-- <div v-else-if="!savedPlan.validStates.selectedPreferences || !savedPlan.validStates.selectedUnits || !savedPlan.validStates.coursePlan" -->
                <div v-else-if="errorList.length > 0"
                  class="page-alert mb-0 warning w-100">
                  <i class="page-alert__icon far fa-exclamation-circle"></i>
                  <div class="page-alert__body">
                    <p>
                      The following {{errorList.length > 1 ? `${errorList.length} errors have` : 'error has' }} been found with your saved plan. <br /></p>
                    <ul>
                      <li v-for="(err, index) in errorList" :key="index">{{err}}</li>
                    </ul>
                    <p>Please select "{{continuingEditingPlan ? 'Continue Editing' : 'Edit Plan'}}" to review and make necessary changes.
                    </p>
                  </div>
                </div>

                <!-- info state -->
                <div v-else-if="unallocatedUnits && unallocatedUnits.length && !validating" class="page-alert mt-2 info w-100">
                  <i class="page-alert__icon far fa-info-circle"></i>
                  <div class="page-alert__body">
                    <p>
                      You have {{unallocatedUnits.length}} unallocated unit{{unallocatedUnits.length === 1 ? '' : 's'}}.
                      <br />
                      Please see the <a href="#" v-on:click="scrollToUnallocated">Unallocated units section</a> below for more details
                    </p>
                  </div>
                </div>

                <!-- no errors state -->
                <div v-else class="page-alert mb-0 success w-100">
                  <i class="page-alert__icon far fa-check-circle"></i>
                  <div class="page-alert__body">
                    <p>
                      Your course plan is still valid. <br />
                      Select "{{continuingEditingPlan ? 'Continue Editing' : 'Edit Plan'}}" to make further changes.
                    </p>
                  </div>
                </div>

                <div class="w-100 d-flex flex-row align-items-end justify-content-center">
                  <router-link tag="button" :to="{ name: 'select-course' }"
                    :class="!this.validationStates.courseValid ? 'btn btn-primary mt-2 flex':'btn btn-secondary mt-2 flex'">
                    <i class="fa fa-angle-left"></i>
                    Create New Plan
                  </router-link>
                  <button v-if="this.validationStates.networkError" class="btn btn-primary mt-2 flex-end" v-on:click="reloadPlan">
                    <i class="fa fa-refresh mr-1"></i>
                    Reload Plan
                  </button>
                  <button v-else-if="this.validationStates.courseValid" class="btn btn-primary mt-2 flex-end" v-on:click="editPlan">
                    <i class="fa fa-link mr-1"></i>
                    {{continuingEditingPlan ? 'Continue Editing' : 'Edit Plan'}}
                  </button>
                </div>
                <div v-if="isReviewer" class="w-100 mt-1 d-flex justify-content-center">
                  <span class="custom-control custom-checkbox">
                    <input type="checkbox" v-model="includeDraftVersions" id="chk-include-versions" class="custom-control-input" :disablxxed="savedPlan.incDrafts" :claxxss="{ disabled: savedPlan.incDrafts}"/>
                    <label class="ml-1 mr-2 custom-control-label modal-course-control-label" for="chk-include-versions">Include proposed units/unit sets</label>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="cplan-completed col-12" v-if="savedPlan.completedUnits && savedPlan.completedUnits.length > 0">
            <div class="cplan-completed__head">
              <div class="cplan-completed__title">
                <i class="far fa-check-circle"></i> Units you've already completed
                <span class="cplan-completed__credits">
                  {{completedTotalCredits}} points completed
                </span>
              </div>
            </div>
            <div class="card-row">
              <!-- refactor this, as we have a completed unit structure. dont need to make a call.  -->
              <SavedPlanCompletedUnitCard v-for="unit in completedSelectedItems" v-bind:key="unit.id"
                :unit="unit" />
            </div>
          </div>

          <!-- Scheduled units... -->
          <div class="col-12" v-if="savedPlan">
            <template v-for="(year, index) in savedPlan.coursePlan.years">
              <div class="cplan-year mb-5" :key="year.teachingPeriod">
                <div class="cplan-year__head">
                  <span class="cplan-year__head-num">Year {{ index + 1 }}</span>
                  <span class="cplan-year__head-year">{{ parseInt(savedPlan.selectedPreferences.startYear) + index }}</span>
                </div>
                <template v-for="teachingPeriod in year.teachingPeriods">
                  <SavedPlanTeachingPeriod v-if="teachingPeriod.units.length !== 0" :key="teachingPeriod.teachingScheduled" :teachingPeriod="teachingPeriod"
                    :teachingPeriodTitle="teachingPeriod.teachingScheduled" :year="index + 1"
                    :teachingPeriodLabel="getTeachingPeriodLabel(teachingPeriod.teachingScheduled)" />
                </template>
              </div>
            </template>
          </div>

          <!-- Unallocated units... -->
          <div v-if="unallocatedUnits && unallocatedUnits.length" class="cplan-unalloc" id="anchor-unallocated" ref="anchorUnallocated">
            <div class="row mt-5">
              <div class="col-12">
                <h4 v-if="unallocatedUnits.length == 1">You have {{ unallocatedUnits.length }} unallocated unit</h4>
                <h4 v-if="unallocatedUnits.length > 1">You have {{ unallocatedUnits.length }} unallocated units</h4>
                <div v-if="unallocatedOfferings.length > 0" class="mt-3">
                  <h5>Unit(s) cannot be scheduled</h5>
                  <div class="row">
                    <div class="col-12 col-lg-9">
                      <p>The following unit(s) cannot be scheduled because there are no valid offerings within the timeframe or the unit(s) have teaching
                        periods
                        which span over the full year. Please contact your Academic Chair below.</p>
                    </div>
                  </div>
                  <div class="card-row">
                    <template v-for="(unit, index) in unallocatedOfferings">
                      <SavedPlanUnallocUnitCard :key="index" :unit="unit" :reviewMode="true" />
                    </template>
                  </div>
                </div>
                <div v-if="unallocatedPreReqs.length > 0" class="mt-3">
                  <h5>Missing pre-requisite</h5>
                  <div class="row">
                    <div class="col-12 col-lg-9">
                      <p>The following unit(s) need pre-requisite unit(s). Please click on the unit(s) below to identify the pre-requisite unit(s), go back to
                        'Select Your Units' and add them to your selection.</p>
                    </div>
                  </div>
                  <div class="card-row">
                    <template v-for="(unit, index) in unallocatedPreReqs">
                      <SavedPlanUnallocUnitCard :key="index" :unit="unit" :reviewMode="true" />
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-5 print-only">
            <h6>Disclaimer</h6>
            <ul>
              <li>The Course Visualiser assists with the generation of a Course Plan. It should not be enacted until it is validated with the Academic Chair of
                the Course.</li>
              <li>This Course Plan is based on the course structure and offerings of latest Approved Courses only. It is the responsibility of students to
                ensure the correct availability of units in each semester/trimester of each academic year.</li>
              <li>Please contact the Academic Chair of the Course if you have any queries relating to the Course Plan.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>

<script>
import useBaseStep from 'client/js/composition/base-step'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import preReqValidator from 'common/helpers/pre-req-validator.js'
import sortUnitsByTitle from 'common/helpers/sort-units-by-title.js'
import moment from 'moment'

import Banner from './common/Banner.vue'
import SavedPlanSummary from './saved-course/SavedPlanSummary.vue'
import SavedPlanTeachingPeriod from './saved-course/SavedPlanTeachingPeriod.vue'
import StudentPreferences from 'client/js/models/student-preferences.js'
import SavedPlanUnallocUnitCard from './saved-course/SavedPlanUnallocUnitCard.vue'
import SavedPlanCompletedUnitCard from './saved-course/SavedPlanCompletedUnitCard.vue'
import ReviewModeInfo from './common/ReviewModeInfo.vue'

export default {
  components: { Banner, SavedPlanSummary, SavedPlanTeachingPeriod, SavedPlanCompletedUnitCard, SavedPlanUnallocUnitCard, ReviewModeInfo },
  setup(props, context) {
    return {
      baseStep: useBaseStep(props, context)
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      validatingCoursePlan: true,
      validCoursePlan: null,
      loadedCourse: null,
      validating: true,
      loadedCourse: null,
      noPlan: false,
      currentValidationTask: null,
      errorList: [],
      coursePlanValidationErrors: []
    }
  },
  computed: {
    ...mapGetters({
      savedPlan: 'savedCourse/getSavedCoursePlan',
      validationStates: 'savedCourse/getValidationStates',
      savedSelectedItems: 'savedCourse/getSavedSelectedItems',
      savedSelectedUnitCodes: 'savedCourse/getSavedSelectedUnitCodes',
      getSelectedCourse: 'courses/getSelectedCourse',
      courseIncDraftVersions: 'courses/includeDraftVersionSetting',
      isAuthenticated: 'global/isAuthenticated',
      isReviewer: 'global/isReviewer',
      loginUrl: 'global/loginUrl'
    }),
    reviewMode: function () {
      return this.savedPlan != null
    },
    completedSelectedItems() {
      const completedUnits = this.savedSelectedItems.filter((unit) => this.savedPlan.completedUnits.includes(unit.code))
      return this.removeDuplicates(completedUnits)
    },
    completedUnitIds() {
      return this.completedSelectedItems.map((unit) => unit.id)
    },
    completedTotalCredits() {
      return this.completedSelectedItems.reduce((total, item) => total + (parseInt(item.creditPoints) || 0), 0)
    },
    requiresLogin() {
      return (this.savedPlan?.status != 'Active' || this.savedPlan?.incDrafts) && !this.isAuthenticated
    },

    allocatedUnitIds() {
      if (this.savedPlan == null) {
        return []
      }
      return this.savedPlan.coursePlan.years.reduce((arr, year) => {
        const units = year.teachingPeriods.reduce((periodUnits, tp) => {
          periodUnits = [...periodUnits, ...tp.units.map((u) => u.id)]
          return periodUnits
        }, [])
        arr = [...arr, ...units]
        return arr
      }, [])
    },
    unallocatedUnits() {
      return this.savedSelectedItems
        .filter((item) => this.allocatedUnitIds.indexOf(item.id) < 0 && !this.completedUnitIds.includes(item.id))
        .sort(sortUnitsByTitle)
        .reduce((total, current) => {
          // Unique based on relationship code
          if (!total.map((x) => x.code).includes(current.code)) {
            total.push(current)
          }
          return total
        }, [])
    },

    unallocatedOfferings() {
      return this.unallocatedUnits.filter(
        (item) =>
          item != null && (item.prerequisites == null || preReqValidator.prerequisitesExistTest(item.code, this.savedSelectedUnitCodes, item.prerequisites))
      )
    },
    unallocatedPreReqs() {
      return this.unallocatedUnits.filter(
        (item) =>
          item != null && item.prerequisites != null && !preReqValidator.prerequisitesExistTest(item.code, this.savedSelectedUnitCodes, item.prerequisites)
      )
    },
    continuingEditingPlan() {
      return (this.$route.query.id || '') == '' && (this.savedPlan?.planId || '') != ''
    },
    formatDateTime() {
      if (this?.savedPlan?.createdOn == null) return 'before 08/2022'

      return moment(this.savedPlan.createdOn).format('DD/MM/YY, h:mm a')
    },
    includeDraftVersions: {
      get() {
        return this.courseIncDraftVersions
      },
      async set(value) {
        this.setCourseIncludeDrafts(value)
        // Force re-validation of the course plan if not already validating
        if (this.savedPlan?.validated && !this.validating) {
          console.log('Re-validating course plan due to include draft versions change')
          this.savedPlan.validated = false
          await this.validateCoursePlan()
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      setValidationStates: 'savedCourse/setValidationStates',
      setSavedCoursePlanValid: 'savedCourse/setSavedCoursePlanValid',
      updateUnitErrors: 'savedCourse/updateUnitErrors',
      setCourseIncludeDrafts: 'courses/setIncludeDrafts'
    }),
    ...mapActions({
      fetchSavedPlan: 'savedCourse/fetchSavedCoursePlan',
      validateAndLoadCourse: 'courses/validateAndLoadCourse',
      validateAndLoadPreferences: 'preferences/validateAndLoadPreferences',
      validateAndLoadUnits: 'units/validateAndLoadUnits',
      updateAndValidatePlan: 'plans/updateAndValidatePlan'
    }),
    removeDuplicates(itemSet) {
      const uniqueIds = []
      const uniqueItems = itemSet.filter((item) => {
        const isDuplicate = uniqueIds.includes(item.id)
        if (!isDuplicate) {
          uniqueIds.push(item.id)
          return true
        }
        return false
      })
      return uniqueItems
    },
    scrollToUnallocated() {
      var unallocPosition = this.$refs.anchorUnallocated.getBoundingClientRect().y
      var offsetMargin = 30
      document.getElementById('wrapperOuter').scrollTop = unallocPosition - offsetMargin
    },
    getTeachingPeriodLabel(code) {
      const teachingPeriod = StudentPreferences.defaultTeachingPeriods.find((tp) => tp.code === code)
      if (teachingPeriod) {
        return teachingPeriod.label
      }
      return code
    },
    async loadPlan(planId) {
      this.error = false
      this.loading = true

      if (planId.length == 0) {
        this.noPlan = true
      }

      try {
        await this.fetchSavedPlan({ id: planId })
        this.loading = false
      } catch (ex) {
        this.error = true
      }

      // console.log('load plan: ' + planId)
    },
    async reloadPlan() {
      console.log('Reloading plan...', this.savedPlan.planId)
      await this.loadPlan(this.savedPlan.planId)
      await this.validateCoursePlan()
    },
    async editPlan() {
      this.baseStep.valid(true)
      this.baseStep.next()
    },
    async validateCoursePlan() {
      if (!this.savedPlan?.validated) {
        // console.log('validating')
        this.validating = true
        // console.log('Validating course plan for', this.savedPlan.planId, this.includeDraftVersions)
        let incDrafts = this.includeDraftVersions

        //get the course from the Curriculum object
        this.currentValidationTask = 'course'
        console.log('Validating', {
            courseId: this.savedPlan.courseId,
            courseLoopVersion: this.savedPlan.status != 'Active' ? this.savedPlan.courseLoopVersion : null, // Only load specific version if the saved plan was a "Draft"
            incDrafts
          })
        try {
          await this.validateAndLoadCourse({
            courseId: this.savedPlan.courseId,
            courseLoopVersion: this.savedPlan.status != 'Active' ? this.savedPlan.courseLoopVersion : null, // Only load specific version if the saved plan was a "Draft"
            incDrafts
          })
        } catch (err) {
          try {
            if (this.savedPlan.status != 'Active') {
              console.log('Validating again without version')
              // If saved plan was a Draft, perhaps it's no longer draft or active - so try again without the Version
              await this.validateAndLoadCourse({
                courseId: this.savedPlan.courseId,
                courseLoopVersion: null,
                incDrafts
              })
              this.setValidationStates({ ...this.validationStates, draftNoLonger: true })
            } else {
              throw (err)
            }
          } catch (err) {
            if (err.response?.status && err.response.status !== 404) {
              // Network or server error, not just "course not found"
              this.setValidationStates({ ...this.validationStates, networkError: true })
              this.validating = false
            } else {
              this.setValidationStates({ ...this.validationStates, courseValid: false })
              this.validating = false
            }
            console.log('Error Loading Course: ', this.savedPlan.courseId, err.message)
            return
          }
        }

        console.log('Validating preferences')
        // validated saved preferences
        this.currentValidationTask = 'selected preferences'
        try {
          await this.validateAndLoadPreferences({
            selectedPreferences: this.savedPlan.selectedPreferences
          })
        } catch (err) {
          this.setValidationStates({ ...this.validationStates, preferencesValid: false })
          console.log('Error Loading preferences', err)
          this.pushError('Error loading preferences')
        }

        // validate saved and completed units
        this.currentValidationTask = 'selected units'
        try {
          await this.validateAndLoadUnits({
            selectedUnits: this.savedPlan.selectedUnits,
            completedUnits: this.savedPlan.completedUnits,
            incDrafts
          })
        } catch (err) {
          this.setValidationStates({ ...this.validationStates, unitsValid: false })
          console.log('Error loading units', err)
          this.pushError('Error loading units')
        }

        // validate course plan object
        this.currentValidationTask = 'course plan'
        try {
          await this.updateAndValidatePlan({
            coursePlan: this.savedPlan.coursePlan
          })
        } catch (err) {
          console.log('Error validating your plan.', err)
          this.coursePlanValidationErrors = err
          this.updateUnitErrors({ errorList: this.coursePlanValidationErrors })
          err.forEach((error) => this.pushError(error.description))
          this.setValidationStates({ ...this.validationStates, planValid: false })
        }

        this.setSavedCoursePlanValid({ valid: true })
        this.validating = false
        // console.log('validated')
      } else {
        this.validating = false
        // console.log('already valid')
      }
    },
    pushError(error) {
      if (!this.errorList.includes(error)) {
        this.errorList.push(error)
      }
    },
    statusLabel(id) {
      return this.__(`label:status ${id.toLowerCase()}`)
    },
    doLogin() {
      window.location.href = this.loginUrl
    }
  },
  async mounted() {
    var queryId = this.$route.query.id || ''
    if (queryId) {
      //query ID exists, load.
      await this.loadPlan(queryId)
    }

    //once loaded, get the saved plan ID
    var existingId = this.savedPlan?.planId || ''
    if (queryId == '' && existingId == '') {
      //we dont have an existing id, and query is empty.
      this.error = true
      return
    }

    if (existingId && !this.requiresLogin) {
      this.error = false
      this.loading = false
      // Update global "include draft versions" setting
      this.includeDraftVersions = this.savedPlan.incDrafts
      await this.validateCoursePlan()
    }
  }
}
</script>

<style></style>
